/**
 * ErrorBoundary - Composant pour capturer les erreurs de rendu React
 * Affiche un message convivial au lieu d'un écran blanc en cas d'erreur
 */
import React from 'react';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    // Met à jour l'état pour afficher l'UI de repli
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Log l'erreur pour debug
    this.setState({ errorInfo });
    
    // Ignore les erreurs de scripts cross-origin
    if (error?.message === 'Script error.' || error?.message === 'Script error') {
      this.setState({ hasError: false, error: null, errorInfo: null });
      return;
    }
    this.reportError(error, errorInfo);
  }

  reportError(error, errorInfo) {
    let user = {};
    try { user = JSON.parse(localStorage.getItem('user') || '{}') || {}; } catch (e) { user = {}; }
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/client-errors`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        message: String(error?.message || error).slice(0, 1000),
        stack: String(error?.stack || '').slice(0, 6000),
        component_stack: String(errorInfo?.componentStack || '').slice(0, 6000),
        url: window.location.pathname.slice(0, 1000),
        user_email: user.email || null,
        user_role: user.role || null,
      }),
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => d?.ref && this.setState({ errorRef: d.ref }))
      .catch(() => {});
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // UI de repli en cas d'erreur
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
          <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-6 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-800 mb-2">
              Une erreur est survenue
            </h2>
            <p className="text-gray-600 mb-4">
              Nous nous excusons pour ce désagrément. L'équipe technique a été informée.
            </p>
            {this.state.errorRef && (
              <p data-testid="error-boundary-ref" className="text-xs text-gray-500 mb-4">
                Référence : {this.state.errorRef} — {String(this.state.error?.message || '').slice(0, 140)}
              </p>
            )}
            <button
              onClick={this.handleRetry}
              className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
            >
              Réessayer
            </button>
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="mt-4 text-left text-xs text-gray-500">
                <summary className="cursor-pointer">Détails de l'erreur</summary>
                <pre className="mt-2 p-2 bg-gray-100 rounded overflow-auto max-h-40">
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
